import { ValidData, ValidDataT } from "@validator/schema";

import * as R from "remeda";
import { format, getHours } from "date-fns";

// Types

type DateRange = [Date, Date] | null;

// Helpers

function getDateRange(dates: Date[]): DateRange {
  const min = R.firstBy(dates, (d) => d.getTime());
  const max = R.firstBy(dates, [(d) => d.getTime(), "desc"]);

  if (min && max) {
    return [min, max];
  }

  return null;
}

function claudeAllUserMessageDates(data: ValidDataT<"claude">): Date[] {
  return R.flatMap(data.content, (r) => {
    return R.pipe(
      r.chat_messages,
      R.filter((m) => m.sender === "human"),
      R.map((m) => new Date(m.created_at)),
    );
  });
}

function chatGptAllUserMessageDates(data: ValidDataT<"chatgpt">): Date[] {
  return R.flatMap(data.content, (r) => {
    return R.pipe(
      r,
      R.prop("mapping"),
      R.entries(),
      R.map(([, v]) => v.message),
      R.filter((v) => v !== null),
      R.filter((v) => v.author.role === "user" && !!v.create_time),
      R.map((v) => new Date(v.create_time! * 1000)),
    );
  });
}

function geminiAllUserMessageDates(data: ValidDataT<"gemini">): Date[] {
  return R.map(data.content, (r) => new Date(r.time));
}

// Conversation date range

function claudeConversationDateRange(data: ValidDataT<"claude">): DateRange {
  const dates = R.map(data.content, (r) => new Date(r.created_at));

  return getDateRange(dates);
}

function chatGptConversationDateRange(data: ValidDataT<"chatgpt">): DateRange {
  const dates = R.map(data.content, (r) => new Date(r.create_time * 1000));

  return getDateRange(dates);
}

function geminiConversationDateRange(data: ValidDataT<"gemini">): DateRange {
  const dates = R.map(data.content, (r) => new Date(r.time));

  return getDateRange(dates);
}

export function conversationDateRange(data: ValidData): DateRange {
  switch (data.source) {
    case "claude":
      return claudeConversationDateRange(data);
    case "chatgpt":
      return chatGptConversationDateRange(data);
    case "gemini":
      return geminiConversationDateRange(data);
  }
}

// Message date range

function claudeUserMessagesDateRange(data: ValidDataT<"claude">): DateRange {
  const dates = claudeAllUserMessageDates(data);
  return getDateRange(dates);
}

function chatGPTUserMessagesDateRange(data: ValidDataT<"chatgpt">): DateRange {
  const dates = chatGptAllUserMessageDates(data);
  return getDateRange(dates);
}

function geminiUserMessagesDateRange(data: ValidDataT<"gemini">): DateRange {
  const dates = geminiAllUserMessageDates(data);
  return getDateRange(dates);
}

export function userMessagesDateRange(data: ValidData): DateRange {
  switch (data.source) {
    case "claude":
      return claudeUserMessagesDateRange(data);
    case "chatgpt":
      return chatGPTUserMessagesDateRange(data);
    case "gemini":
      return geminiUserMessagesDateRange(data);
  }
}

// Daily messsage count

export type MessagesPerDay = { date: string; count: number }[];

function dailyMessageCount(dates: Date[]): MessagesPerDay {
  const grouped = R.groupBy(dates, (d) => format(d, "yyyy-MM-dd"));
  return R.pipe(
    Object.entries(grouped),
    R.map(([date, items]) => ({ date, count: items.length })),
    R.sortBy((x) => x.date),
  );
}

function claudeMessagesPerDay(data: ValidDataT<"claude">): MessagesPerDay {
  const dates = claudeAllUserMessageDates(data);
  return dailyMessageCount(dates);
}

function chatGptMessagesPerDay(data: ValidDataT<"chatgpt">): MessagesPerDay {
  const dates = chatGptAllUserMessageDates(data);
  return dailyMessageCount(dates);
}

function geminiMessagesPerDay(data: ValidDataT<"gemini">): MessagesPerDay {
  const dates = geminiAllUserMessageDates(data);
  return dailyMessageCount(dates);
}

export function messagesPerDay(data: ValidData): MessagesPerDay | null {
  switch (data.source) {
    case "claude":
      return claudeMessagesPerDay(data);
    case "chatgpt":
      return chatGptMessagesPerDay(data);
    case "gemini":
      return geminiMessagesPerDay(data);
  }
}

// Total messages sent

function claudeUserMessageCount(data: ValidDataT<"claude">) {
  const ls = R.map(data.content, (r) => {
    return R.filter(r.chat_messages, (m) => m.sender === "human").length;
  });

  return R.reduce(ls, (x, y) => x + y, 0);
}

function chatGptUserMessageCount(data: ValidDataT<"chatgpt">) {
  const ls = R.map(data.content, (r) => {
    return R.pipe(
      r,
      R.prop("mapping"),
      R.entries(),
      R.map(([, v]) => v.message),
      R.filter((v) => v !== null),
      R.filter((v) => v.author.role === "user"),
    ).length;
  });

  return R.reduce(ls, (x, y) => x + y, 0);
}

export function userMessageCount(data: ValidData): number {
  switch (data.source) {
    case "claude":
      return claudeUserMessageCount(data);
    case "chatgpt":
      return chatGptUserMessageCount(data);
    case "gemini":
      return data.content.length;
  }
}

// Total conversations

export function conversationCount(data: ValidData): number | null {
  switch (data.source) {
    case "claude":
      return data.content.length;
    case "chatgpt":
      return data.content.length;
    case "gemini":
      return null;
  }
}

// Time of day usage

export interface TimeofDayUsage {
  times: Date[];
  lateTimes: Date[];
  grouped: Record<number, Date[]>;
  labeled: [string, Date[]][];
}

function hourLabel(hour: number): string {
  if (hour === 0) return "12a";
  if (hour === 12) return "12p";
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}

function groupDatesByHour(dates: Date[], _offset: number): Record<number, Date[]> {
  return R.groupBy(dates, (d) => getHours(d));
  // return R.groupBy(dates, (d) => (d.getUTCHours() + offset + 24) % 24);
}

function calculateTimeOfDayUsage(dates: Date[], offset: number): TimeofDayUsage {
  const grouped = groupDatesByHour(dates, offset);
  const lateTimes = R.reduce(
    [22, 23, 0, 1, 2, 3, 4, 5],
    (acc, curr) => {
      const ts = grouped[curr] || [];
      return [...acc, ...ts];
    },
    [] as Date[],
  );

  const labeled: [string, Date[]][] = Array.from({ length: 24 }, (_, h) => [
    hourLabel(h),
    grouped[h] ?? [],
  ]);

  return {
    times: dates,
    lateTimes,
    grouped,
    labeled,
  };
}

function claudeTimeOfDayUsage(data: ValidDataT<"claude">, offset: number): TimeofDayUsage {
  const dates = claudeAllUserMessageDates(data);
  return calculateTimeOfDayUsage(dates, offset);
}

function chatGptTimeOfDayUsage(data: ValidDataT<"chatgpt">, offset: number): TimeofDayUsage {
  const dates = chatGptAllUserMessageDates(data);
  return calculateTimeOfDayUsage(dates, offset);
}

function geminiTimeOfDayUsage(data: ValidDataT<"gemini">, offset: number): TimeofDayUsage {
  const dates = R.map(data.content, (r) => new Date(r.time));
  return calculateTimeOfDayUsage(dates, offset);
}

export function timeOfDayUsage(data: ValidData, offset: number): TimeofDayUsage | null {
  switch (data.source) {
    case "claude":
      return claudeTimeOfDayUsage(data, offset);
    case "chatgpt":
      return chatGptTimeOfDayUsage(data, offset);
    case "gemini":
      return geminiTimeOfDayUsage(data, offset);
  }
}
